import { useMemo, useState } from 'react';

import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import LWForm, { FormErrors, FormSubmitHandler } from 'components/LWForm';
import useAssetList from 'modules/queries/asset/useList';
import useCaseCreationPicklistOptions from 'modules/queries/support/request/useCaseCreationPicklistOptions';
import useSupportRequestCreate from 'modules/queries/support/request/useCreate';
import toast from '@liquidweb-dev/undercurrent/toast';
import Block from '@liquidweb-dev/undercurrent/Block';
import BlockContent from '@liquidweb-dev/undercurrent/BlockContent';
import Box from '@liquidweb-dev/undercurrent/Box';
import BuildingIcon from '@liquidweb-dev/undercurrent/icons/lucide/Building';
import Button from '@liquidweb-dev/undercurrent/Button';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CardHeader from '@liquidweb-dev/undercurrent/CardHeader';
import DialogActions from '@liquidweb-dev/undercurrent/DialogActions';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import ServerIcon from '@liquidweb-dev/undercurrent/icons/lucide/Server';
import ShapesIcon from '@liquidweb-dev/undercurrent/icons/lucide/Shapes';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import { autocompleteClasses } from '@liquidweb-dev/undercurrent/Autocomplete';
import useSendHubspotEvent from 'utility/gtm/hubspot/useEvent';
import { HookedSubmit, HookedTextField } from '../Form';
import HookedAutocomplete from '../Form/HookedAutocomplete';
import HookedTextFieldList from '../Form/HookedTextFieldList';

interface CreateAssetLabelParams {
	domain: string;
	type: string;
}

export const createAssetLabel = ({
	domain,
	type,
}: CreateAssetLabelParams): string => {
	if (type === 'DREG') {
		return `Domain Registration: ${domain}`;
	}

	if (type === 'DNS.ZONE') {
		return `DNS: ${domain}`;
	}

	if (type === 'SSL.DV') {
		return `SSL: ${domain}`;
	}

	return domain;
};

interface AssetOption {
	uniqId: string;
	label: string;
}

export interface DialogCreateSupportCaseProps
	extends Omit<DialogProps, 'onClose'> {
	relatedTicketId?: string;
	initSubject?: string;
	initDepartment?: string;
	initCategory?: string;
	initAssetOption?: AssetOption;
	initRecipients?: string[];
	onClose?: () => void;
}

function DialogCreateSupportCase({
	onClose,
	open,
	relatedTicketId,
	initSubject,
	initDepartment,
	initCategory,
	initAssetOption,
	initRecipients,
	...dialogProps
}: DialogCreateSupportCaseProps) {
	const initFormValues = {
		subject: initSubject || '',
		department: initDepartment || '',
		category: initCategory || '',
		asset: initAssetOption || null,
		recipients: initRecipients || [],
		comments: '',
	};

	const [formValues, setFormValues] = useState(initFormValues);
	const [formErrors, setFormErrors] = useState<Partial<FormErrors>>({});

	const { data: caseCreationPicklistOptions } = useCaseCreationPicklistOptions({
		enabled: Boolean(open),
	});
	const { data: assetsData, isLoading: assetsIsLoading } = useAssetList(
		{
			page_size: 9999,
		},
		{ enabled: Boolean(open) },
	);

	const assets = useMemo((): AssetOption[] => {
		if (!assetsData?.items) {
			return [];
		}

		return assetsData.items.map(({ domain, type, uniq_id: uniqId }) => {
			const asset: AssetOption = {
				uniqId,
				label: createAssetLabel({ domain, type }),
			};
			return asset;
		});
	}, [assetsData]);

	const departments = caseCreationPicklistOptions?.departments || [];
	const categories =
		caseCreationPicklistOptions?.categoriesByDepartments[
			formValues.department
		] || [];

	const {
		mutate: createSupportRequest,
		isLoading: createSupportRequestIsLoading,
	} = useSupportRequestCreate();

	const sendTicketEvent = useSendHubspotEvent('ticket');

	const handleClose = () => {
		setFormValues(initFormValues);
		setFormErrors({});
		onClose?.();
	};

	const handleSubmit: FormSubmitHandler<typeof initFormValues> = (vals) => {
		createSupportRequest(
			{
				status: 'open',
				desired_lw_team: vals.department,
				description: vals.comments,
				uniq_id: vals.asset?.uniqId,
				subject: vals.subject,
				type: caseCreationPicklistOptions?.ticketCreationTypeByDepartment[
					vals.department
				],
				sub_type: categories.length > 0 ? vals.category : undefined,
				cc_list: vals.recipients,
				related_ticket_id: relatedTicketId,
			},
			{
				onSuccess: () => {
					handleClose();
					sendTicketEvent({
						serverUniqId: vals.asset?.uniqId,
						serverHostName: vals.asset?.label,
					});
					toast.success('Case created successfully');
				},
			},
		);
	};

	return (
		<Dialog fullScreen onClose={handleClose} open={open} {...dialogProps}>
			<LWForm
				values={formValues}
				onValuesChange={setFormValues}
				errors={formErrors}
				onErrorsChange={setFormErrors}
				onSubmit={handleSubmit}
				validators={{
					recipients: (value, otherValues) => {
						if (
							otherValues?.recipients.some(
								// @ts-expect-error we need to update the validator function in the Hooked components
								(otherRecipient) => otherRecipient === value,
							)
						) {
							return 'You’ve already added that email';
						}
						return '';
					},
				}}
				style={{
					display: 'flex',
					flexDirection: 'column',
					flexGrow: 1,
				}}
			>
				<DialogTitle onClose={handleClose}>Create a case</DialogTitle>
				<DialogContent>
					<Stack spacing={300}>
						<HookedTextField
							name="subject"
							label="Subject"
							placeholder="Name your case"
							required
						/>

						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={100}>
							<HookedTextField
								name="department"
								select
								required
								slotProps={{
									input: {
										startAdornment: (
											<InputAdornment position="start">
												<BuildingIcon />
											</InputAdornment>
										),
									},
									select: {
										displayEmpty: true,
									},
								}}
							>
								<MenuItem value="" disabled sx={{ display: 'none' }}>
									Choose department
								</MenuItem>
								{departments.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</HookedTextField>

							{categories.length > 0 && (
								<HookedTextField
									name="category"
									select
									required
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<ShapesIcon />
												</InputAdornment>
											),
										},
										select: {
											displayEmpty: true,
										},
									}}
								>
									<MenuItem value="" disabled sx={{ display: 'none' }}>
										Choose category
									</MenuItem>
									{categories.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</HookedTextField>
							)}

							<HookedAutocomplete
								options={assets}
								disabled={assetsIsLoading}
								getOptionKey={(option: AssetOption) => option.uniqId}
								isOptionEqualToValue={(
									option: AssetOption,
									value: AssetOption,
								) => option.uniqId === value.uniqId}
								name="asset"
								placeholder="Choose product"
								disablePortal
								autoWidth={{ minWidth: 220, maxWidth: 440 }}
								startAdornment={
									<InputAdornment position="start">
										<ServerIcon />
									</InputAdornment>
								}
								sx={({ vars }) => ({
									[`& .${autocompleteClasses.inputRoot}`]: {
										[`& ::placeholder`]: {
											color: vars.palette.uc.text.main,
											opacity: 1,
										},
									},
								})}
							/>
						</Stack>

						<Stack direction={{ xs: 'column', md: 'row' }} spacing={300}>
							<Stack sx={{ flexGrow: 2 }}>
								<Block>
									<BlockContent>
										<HookedTextField
											fullWidth
											name="comments"
											label="Start the conversation"
											placeholder="Describe what you need help with"
											required
											multiline
											minRows={9}
											maxRows={20}
										/>
									</BlockContent>
								</Block>
							</Stack>

							<Box sx={{ flexGrow: 1 }}>
								<Card>
									<CardHeader title="Watchers" />
									<CardContent>
										<HookedTextFieldList
											type="email"
											name="recipients"
											placeholder="Add emails"
										/>
									</CardContent>
								</Card>
							</Box>
						</Stack>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} variant="ghost" size="large">
						Cancel
					</Button>
					<HookedSubmit size="large" loading={createSupportRequestIsLoading}>
						Create case
					</HookedSubmit>
				</DialogActions>
			</LWForm>
		</Dialog>
	);
}

export default DialogCreateSupportCase;
